import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Row, Col, Button, Space, DatePicker } from "antd";
import { ReloadOutlined, FullscreenOutlined } from "@ant-design/icons";
import FSMServiceRequestPipeline from "./FSMDashboardParts/FSMServiceRequestPipeline";
import FSMFormMetrics from "./FSMDashboardParts/FSMFormMetrics";
import FSMPerformanceTrends from "./FSMDashboardParts/FSMPerformanceTrends";
import FSMAlertsSummary from "./FSMDashboardParts/FSMAlertsSummary";
import FSMResourceStatus from "./FSMDashboardParts/FSMResourceStatus";
import FSMTaskDistribution from "./FSMDashboardParts/FSMTaskDistribution";
import FSMMonthlyComparison from "./FSMDashboardParts/FSMMonthlyComparison";
import trans from "../../utils";
import dayjs from "dayjs";
import AccessDenied from "../AccessDenied";
import usePermission from "../../hooks/usePermission";

export default function FSMDashboard() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs().endOf("month"));

  const { view: canViewServiceRequest } = usePermission("REPORTSFSSERVICEREQUEST");

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
      setLastUpdated(new Date());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs().endOf("month"));
    setLastUpdated(new Date());
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  // const handleDateRangeChange = (dates) => {
  //   if (dates && dates.length > 0) {
  //     setFromDate(dates[0]);
  //     setToDate(dates[1]);
  //   } else {
  //     setFromDate(null);
  //     setToDate(null);
  //   }
  // };

  return (
    <div>
      <Helmet>
        <title>Field Service Management Dashboard</title>
        <meta
          name="description"
          content="Comprehensive FSM Dashboard for managing field operations, technicians, and service requests"
        />
      </Helmet>

      {/* Dashboard Header */}
      <div
        className="d-flex justify-content-between align-items-center mb-4 p-3"
        style={{ background: "#FFFFFFDE" }}
      >
        <div>
          <h2 className="fs-24 fw-700 neutral-800 mb-1">
            {trans("Field Service Management Dashboard")}
          </h2>
          <p className="fs-14 neutral-500 mb-0">
            {trans("Last updated:")} {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
        <Space>
          {/* <DatePicker.RangePicker
            onChange={handleDateRangeChange}
            placeholder={[trans("Start Date"), trans("End Date")]}
            style={{ width: 250 }}
            value={fromDate && toDate ? [fromDate, toDate] : []}
          /> */}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            type="default"
          >
            {trans("Refresh")}
          </Button>
          <Button
            icon={<FullscreenOutlined />}
            onClick={toggleFullscreen}
            type="default"
          >
            {isFullscreen ? trans("Exit Fullscreen") : trans("Fullscreen")}
          </Button>
        </Space>
      </div>

      {/* Alert Summary - Top Priority */}
      <div style={{ background: "#FFFFFFDE" }} className="p-3">
        <div className="mb-4">
          <FSMAlertsSummary
            key={`alerts-${refreshKey}`}
            refreshKey={refreshKey}
            canViewServiceRequest={canViewServiceRequest}
          />
        </div>

        {/* Resource Status and Task Distribution */}
        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} lg={12}>
            <FSMResourceStatus
              key={`resource-${refreshKey}`}
              refreshKey={refreshKey}
            />
          </Col>
          <Col xs={24} lg={12}>
            <FSMTaskDistribution
              key={`distribution-${refreshKey}`}
              fromDate={fromDate}
              toDate={toDate}
            />
          </Col>
        </Row>

        {/* Form Metrics + Service Request Pipeline (or Performance Trends if no SR permission) */}
        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} lg={12}>
            <FSMFormMetrics
              key={`form-metrics-${refreshKey}`}
              fromDate={fromDate}
              toDate={toDate}
            />
          </Col>
          <Col xs={24} lg={12}>
            {canViewServiceRequest ? (
              <FSMServiceRequestPipeline
                key={`pipeline-${refreshKey}`}
                fromDate={fromDate}
                toDate={toDate}
              />
            ) : (
              <FSMPerformanceTrends
                key={`trends-${refreshKey}`}
                refreshKey={refreshKey}
              />
            )}
          </Col>
        </Row>

        {/* Performance Trends + Monthly Comparison (or just Monthly if SR hidden) */}
        <Row gutter={[16, 16]} className="mb-4">
          {canViewServiceRequest && (
            <Col xs={24} lg={12}>
              <FSMPerformanceTrends
                key={`trends-${refreshKey}`}
                refreshKey={refreshKey}
              />
            </Col>
          )}
          <Col xs={24} lg={12}>
            <FSMMonthlyComparison
              key={`monthly-${refreshKey}`}
              refreshKey={refreshKey}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}
